import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { CHIP } from "./AdminView";

export default function AgentDashboard({ navigate }) {
  const { user, logout, refreshMe } = useAuth();
  const { applications } = useApp();
  const [search, setSearch] = useState("");

  // pull the latest wallet balance (admin may have updated it since this session started)
  useEffect(() => { refreshMe(); }, []); 

  const myApps = applications.filter(a => a.agent_email === user?.email);
  const filtered = myApps.filter(a => [a.id,a.full_name,a.destination,a.service_type].join(" ").toLowerCase().includes(search.toLowerCase()));

  const stats = {
    total:      myApps.length,
    pending:    myApps.filter(a=>a.status==="Pending").length,
    inProcess:  myApps.filter(a=>a.status==="In Process").length,
    approved:   myApps.filter(a=>a.status==="Approved").length,
    action:     myApps.filter(a=>a.status==="Rejected"||a.status==="Modify Required").length,
  };

  const StatCard = ({label,val,icon,color}) => (
    <div style={{background:"#fff",borderRadius:14,padding:"18px 20px",border:"1px solid #e4e9f0",position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:color}}/>
      <div style={{fontSize:24,marginBottom:8}}>{icon}</div>
      <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:34,fontWeight:700,color:"#0d1f3c",lineHeight:1}}>{val}</div>
      <div style={{fontSize:10,letterSpacing:"1.5px",textTransform:"uppercase",color:"#6b7a90",marginTop:6}}>{label}</div>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:"#f0f3f9",fontFamily:"'DM Sans',sans-serif"}}>
      <style>{`
        @media (max-width:768px){
          .ad-nav{padding:0 16px !important;}
          .ad-agency-badge{display:none !important;}
          .ad-wrap{padding:16px !important;}
          .ad-header{flex-direction:column !important;align-items:flex-start !important;}
        }
      `}</style>

      {/* NAV */}
      <nav className="ad-nav" style={{background:"#0d1f3c",padding:"0 36px",height:62,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:100,borderBottom:"2px solid #4a7fb5"}}>
        <img src="/logo-small.png" alt="AIHOVI" style={{height:54,objectFit:"contain",filter:"drop-shadow(0 1px 6px rgba(0,0,0,0.3))"}} onError={e=>e.target.style.display="none"}/>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <div title="Amount paid, as confirmed by admin" style={{background:"linear-gradient(135deg,#1c3660,#2a4570)",border:"1px solid rgba(74,127,181,0.4)",borderRadius:24,padding:"6px 16px",fontSize:13,color:"#fff",display:"flex",alignItems:"center",gap:7,fontWeight:700}}>
            <span style={{fontSize:14}}>💰</span>
            ₹{Number(user?.wallet_balance || 0).toLocaleString("en-IN")}
          </div>
          <div className="ad-agency-badge" style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(74,127,181,0.3)",borderRadius:24,padding:"5px 14px",fontSize:12,color:"#fff",display:"flex",alignItems:"center",gap:7}}>
            <span style={{width:7,height:7,borderRadius:"50%",background:"#4CAF50",display:"inline-block",flexShrink:0}}/>
            {user?.name} · {user?.agency}
          </div>
          <button onClick={logout} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.2)",color:"#fff",padding:"6px 15px",borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>Sign Out</button>
        </div>
      </nav>

      <div className="ad-wrap" style={{maxWidth:1100,margin:"0 auto",padding:"26px 36px 60px"}}>
        {user?.justApproved && (
          <div style={{background:"#dcfce7",border:"1px solid #22c55e",color:"#166534",padding:"12px 16px",borderRadius:10,fontSize:13,marginBottom:18}}>
            ✅ Your account has been approved by admin. You are now active and can access the portal.
          </div>
        )}
        <div className="ad-header" style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22,gap:12,flexWrap:"wrap"}}>
          <div>
            <h1 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,fontWeight:700,color:"#0d1f3c"}}>My Applications</h1>
            <p style={{fontSize:13,color:"#6b7a90",marginTop:2}}>Track the status of all applications you've submitted</p>
          </div>
          <button onClick={()=>navigate("form")} style={{padding:"12px 22px",background:"linear-gradient(135deg,#4a7fb5,#d4736a)",color:"#fff",border:"none",borderRadius:10,fontSize:13,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>+ New Application</button>
        </div>

        {/* STATS */}
        <div className="rgrid-4" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:22}}>
          <StatCard label="Total" val={stats.total} icon="📁" color="linear-gradient(90deg,#4a7fb5,#8a9fc4)"/>
          <StatCard label="Pending / In Process" val={stats.pending+stats.inProcess} icon="⏳" color="linear-gradient(90deg,#f59e0b,#fbbf24)"/>
          <StatCard label="Approved" val={stats.approved} icon="✅" color="linear-gradient(90deg,#22c55e,#4ade80)"/>
          <StatCard label="Needs Your Attention" val={stats.action} icon="⚠️" color="linear-gradient(90deg,#ef4444,#f87171)"/>
        </div>

        {/* TABLE */}
        <div style={{background:"#fff",borderRadius:16,border:"1px solid #e4e9f0",overflow:"hidden"}}>
          <div style={{padding:"18px 22px",borderBottom:"1px solid #eaeef5",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
            <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,fontWeight:600,color:"#0d1f3c"}}>Application Records</h2>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search name, ID, destination..."
              style={{padding:"8px 14px",border:"1.5px solid #dde3ed",borderRadius:8,fontSize:13,outline:"none",fontFamily:"'DM Sans',sans-serif",width:220}}/>
          </div>

          {filtered.length>0 ? (
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead style={{background:"#f6f8fc"}}>
                  <tr>
                    {["App ID","Applicant","Service","Destination","Submitted","Status","Reason / Note","Action"].map(h=>(
                      <th key={h} style={{padding:"12px 14px",textAlign:"left",fontSize:10,letterSpacing:"1.5px",textTransform:"uppercase",color:"#8a9bb5",fontWeight:600,borderBottom:"1px solid #eaeef5"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice().reverse().map(app=>(
                    <tr key={app.id}>
                      <td style={{padding:"13px 14px",borderBottom:"1px solid #f0f3f9"}}><strong style={{fontFamily:"'Cormorant Garamond',serif",color:"#0d1f3c",fontSize:13}}>{app.id}</strong></td>
                      <td style={{padding:"13px 14px",borderBottom:"1px solid #f0f3f9",fontSize:13}}>{app.full_name}</td>
                      <td style={{padding:"13px 14px",borderBottom:"1px solid #f0f3f9"}}><span style={{background:"#e0ecff",color:"#2563eb",padding:"3px 9px",borderRadius:6,fontSize:11,fontWeight:500}}>{app.service_type}</span></td>
                      <td style={{padding:"13px 14px",borderBottom:"1px solid #f0f3f9",fontSize:13}}>{app.destination}</td>
                      <td style={{padding:"13px 14px",borderBottom:"1px solid #f0f3f9",fontSize:12,color:"#6b7a90"}}>{app.submitted_at}</td>
                      <td style={{padding:"13px 14px",borderBottom:"1px solid #f0f3f9"}}>
                        <span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"4px 10px",borderRadius:20,fontSize:11,fontWeight:600,background:CHIP[app.status]?.bg,color:CHIP[app.status]?.c}}>
                          {CHIP[app.status]?.icon} {app.status}
                        </span>
                      </td>
                      <td style={{padding:"13px 14px",borderBottom:"1px solid #f0f3f9",fontSize:12,color:"#6b7a90",maxWidth:220}}>
                        {app.statusNote ? <span style={{color:"#c0392b"}}>{app.statusNote}</span> : "—"}
                      </td>
                      <td style={{padding:"13px 14px",borderBottom:"1px solid #f0f3f9"}}>
                        <button onClick={()=>navigate("agent-view",{app_id:app.id})} style={{padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:600,background:"#e0ecff",color:"#2563eb",border:"none",cursor:"pointer"}}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{padding:60,textAlign:"center"}}>
              <div style={{fontSize:48,marginBottom:14}}>📭</div>
              <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:24,color:"#0d1f3c",marginBottom:8}}>{search?"No results found":"No Applications Yet"}</h3>
              <p style={{fontSize:13,color:"#6b7a90",marginBottom:18}}>{search?"Try different search terms":"Applications you submit will appear here with live status updates."}</p>
              {!search && <button onClick={()=>navigate("form")} style={{padding:"11px 22px",background:"linear-gradient(135deg,#4a7fb5,#d4736a)",color:"#fff",border:"none",borderRadius:10,fontSize:13,fontWeight:600,cursor:"pointer"}}>+ Submit Your First Application</button>}
            </div>
          )}
        </div>

        <div style={{textAlign:"center",fontSize:11,color:"#b0b8c8",marginTop:24}}>
          Developed by <a href="https://www.websignx.com" target="_blank" rel="noreferrer" style={{color:"#8a9bb5",textDecoration:"none",fontWeight:600}}>www.websignx.com</a>
        </div>
      </div>
    </div>
  );
}
